/*
 * TL Essentials Club cart features (Discount Function Spec sections 4, 5, 6b).
 *
 * - Membership toggle in the cart drawer (selling_plan 6088654942 required).
 * - GWP auto add/remove at the $250 threshold.
 * - PDP add-on toggle; default variant Classic/L; star click scrolls to reviews;
 *   fair pricing bar animation; fit hider (Classic-only colors); 3XL hider under Modern;
 *   Modern S sell-through hider (product .js endpoint; options are OBJECTS — read opt.name);
 *   pre-order tidy (tlPreorderTidy - still live, still called from tlPDPUpdate.
 *   It matches on TEXT, not on any app markup, so it now keys on our own
 *   .tl-preorder-note: it hides "Re-stocking soon" when a visible sibling block
 *   matches /pre-?ordered item|shipment starts/. snippets/inventory.liquid now
 *   suppresses the re-stocking line server-side for pre-order variants, so this
 *   is largely redundant; the only case it could still cover is the no-date
 *   fallback note, whose wording its regex does not match);
 *   CSS injection (swatch sizing, chip hover/contrast); WeFunder new-tab;
 *   star gate <10 reviews (persistent observer — Okendo re-mounts its widget, so the
 *   gate re-applies for 15s instead of disconnecting on first success);
 *   sticky-bar Sold out -> Join the Waitlist;
 *   header logo sized to match live (inline style; Aug 4).
 *
 * PDP updates run from BOTH a document change listener AND a MutationObserver on
 * .product-info (Prestige replaces <variant-picker> via AJAX re-renders).
 */
(function () {
  'use strict';

  var MEMBERSHIP_VARIANT_ID = 39505661919326;
  var MEMBERSHIP_SELLING_PLAN = 6088654942;
  /* Sept 26 2026: the $0.00 placeholder bag (variant 39947840356446) was archived,
     so the auto-add below failed silently and the gift was broken. We now add the
     REAL $30 Travel Laundry Bag and let an automatic discount zero it at $250, so
     stock decrements. Gifted and purchased bags are the same variant, so the add
     stamps the _gwp line item property - that property, not the handle, is what
     every other file uses to tell the gift apart. Nothing else here changed. */
  var GWP_VARIANT_ID = 39685541593182;
  var GWP_THRESHOLD_CENTS = 25000;

  function shopRoot() {
    return window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
  }

  async function mutateCart(path, body) {
    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    var response = await fetch(shopRoot() + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(Object.assign({}, body, { sections: sectionsToBundle.join(',') }))
    });

    var responseJson = await response.json();
    if (!response.ok) {
      var error = new Error(responseJson.description || 'Cart request failed');
      error.responseJson = responseJson;
      throw error;
    }

    var cartContent = await (await fetch(shopRoot() + 'cart.js')).json();
    cartContent.sections = responseJson.sections;

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'tl:cart-update',
          cart: cartContent
        }
      })
    );

    return cartContent;
  }

  /* Membership toggle (spec section 4) */

  var membershipBusy = false;

  document.addEventListener('change', function (event) {
    var input = event.target.closest ? event.target.closest('[data-tl-club-input]') : null;
    if (!input) {
      return;
    }
    if (membershipBusy) {
      input.checked = !input.checked;
      return;
    }
    membershipBusy = true;
    input.setAttribute('aria-busy', 'true');

    var run;
    if (input.checked) {
      run = mutateCart('cart/add.js', {
        id: MEMBERSHIP_VARIANT_ID,
        quantity: 1,
        selling_plan: MEMBERSHIP_SELLING_PLAN
      });
    } else {
      var row = input.closest('[data-tl-club-toggle]');
      var lineKey = row ? row.getAttribute('data-membership-line-key') : '';
      run = lineKey
        ? mutateCart('cart/change.js', { id: lineKey, quantity: 0 })
        : Promise.resolve();
    }

    run.catch(function () {
      input.checked = !input.checked;
    }).then(function () {
      input.removeAttribute('aria-busy');
      membershipBusy = false;
    });
  });

  /* GWP auto add/remove at the $250 boundary (spec section 5) */

  var gwpBusy = false;

  function syncGwp() {
    if (gwpBusy) {
      return;
    }
    var bar = document.querySelector('[data-tl-gwp]');
    if (!bar) {
      return;
    }
    var totalCents = parseInt(bar.getAttribute('data-total-cents'), 10) || 0;
    var lineKey = bar.getAttribute('data-gwp-line-key') || '';
    var gwpQuantity = parseInt(bar.getAttribute('data-gwp-qty'), 10) || 0;

    var run = null;
    if (totalCents >= GWP_THRESHOLD_CENTS && !lineKey) {
      run = mutateCart('cart/add.js', { id: GWP_VARIANT_ID, quantity: 1, properties: { _gwp: 'Free Travel Laundry Bag' } });
    } else if (totalCents < GWP_THRESHOLD_CENTS && lineKey) {
      run = mutateCart('cart/change.js', { id: lineKey, quantity: 0 });
    } else if (lineKey && gwpQuantity > 1) {
      run = mutateCart('cart/change.js', { id: lineKey, quantity: 1 });
    }

    if (!run) {
      return;
    }
    gwpBusy = true;
    run.catch(function () {}).then(function () {
      gwpBusy = false;
    });
  }

  document.addEventListener('cart-drawer:refreshed', syncGwp);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGwp);
  } else {
    syncGwp();
  }

  /* PDP add-on toggle (spec section 6b) */

  document.addEventListener('change', function (event) {
    var check = event.target.closest ? event.target.closest('[data-tl-addon-check]') : null;
    if (!check) {
      return;
    }
    var wrap = check.closest('[data-tl-addon]');
    var quantityInput = wrap ? wrap.querySelector('[data-tl-addon-qty]') : null;
    if (quantityInput) {
      quantityInput.value = check.checked ? '2' : '1';
    }
  });

  /* TL: default landing variant Fit=Classic / Size=L */

  function tlClickOptionByLabel(text) {
    var inputs = document.querySelectorAll('.product-info .variant-picker input[type="radio"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var label = input.id ? document.querySelector('label[for="' + input.id + '"]') : null;
      var labelText = label ? label.textContent.replace(/\s+/g, ' ').trim() : '';
      if (labelText === text) {
        if (!input.checked && !input.disabled && !label.classList.contains('is-disabled')) {
          input.click();
        }
        return;
      }
    }
  }

  function tlDefaultVariant() {
    if (!document.querySelector('.product-info .variant-picker')) {
      return;
    }
    if (new URLSearchParams(window.location.search).has('variant')) {
      return;
    }
    tlClickOptionByLabel('Classic');
    window.setTimeout(function () {
      tlClickOptionByLabel('L');
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tlDefaultVariant);
  } else {
    tlDefaultVariant();
  }

  /* TL: star rating click scrolls to the reviews section */

  document.addEventListener('click', function (event) {
    if (!event.target.closest) {
      return;
    }
    var star = event.target.closest('.product-info .okeReviews, .product-info [data-oke-star-rating], .product-info .oke-sr');
    if (!star) {
      return;
    }
    var reviews = document.querySelector('.shopify-section--apps .okeReviews, [id*="__reviews"], .oke-w');
    if (reviews) {
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* TL: fair pricing bars animate in when scrolled into view */

  function tlFairObserver() {
    var fair = document.querySelector('[data-tl-fair]');
    if (!fair) {
      return;
    }
    if (!('IntersectionObserver' in window)) {
      fair.classList.add('is-inview');
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35 });
    observer.observe(fair);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tlFairObserver);
  } else {
    tlFairObserver();
  }
/* TL: update fair pricing block when variant changes (Aug 7) */
function tlFairUpdatePrices(priceCents) {
  var fair = document.querySelector('[data-tl-fair]');
    if (!fair) return;
      var fmt = function(c) { return Shopify.formatMoney(c, '${{amount}}'); };
        var clubCents = Math.round(priceCents * 0.70);
          fair.querySelectorAll('.tl-fair__price').forEach(function(el) {
              if (!el.classList.contains('tl-fair__price--muted') && !el.classList.contains('tl-fair__price--clubv2')) {
                    el.textContent = fmt(priceCents);
                        }
                          });
                            fair.querySelectorAll('.tl-fair__price--clubv2').forEach(function(el) {
                                el.textContent = fmt(clubCents);
                                  });
                                    var barTl = fair.querySelector('.tl-fair__bar--tl');
                                      if (barTl) { barTl.style.setProperty('--tl-w', Math.round(priceCents / 90) + '%'); }
                                        var barClub = fair.querySelector('.tl-fair__bar--club');
                                          if (barClub) { barClub.style.setProperty('--tl-w', Math.round(clubCents / 90) + '%'); }
                                          }
                                          document.addEventListener('variant:changed', function(e) {
                                            if (e.detail && e.detail.variant && e.detail.variant.price != null) {
                                                tlFairUpdatePrices(e.detail.variant.price);
                                                  }
                                                  });

  /* TL: inject CSS -- swatch sizing, chip hover polish, availability contrast (Aug 4). */

  (function () {
    if (document.getElementById('tl-club-styles')) { return; }
    var s = document.createElement('style');
    s.id = 'tl-club-styles';
    s.textContent =
      '.product-info .thumbnail-swatch{width:52px!important;height:52px!important;}' +
      '.product-info .thumbnail-swatch img{width:52px!important;height:52px!important;object-fit:cover;}' +
      '.product-info variant-picker .color-swatch{width:52px!important;height:52px!important;}' +
      '.product-info .block-swatch{transition:border-color .15s ease,color .15s ease;}' +
      '.product-info input[type="radio"]:not(:checked)+label.block-swatch:not(.is-disabled){color:#121212;}' +
      '.product-info label.block-swatch.is-disabled{opacity:.6!important;}' +
      '@media (hover:hover){.product-info .block-swatch:not(.is-disabled):hover{border-color:#121212;}}';
    document.head.appendChild(s);
  })();

  /* TL: header logo sized to match the live site (Aug 4, Robbie). Inline style wins. */

  (function () {
    function tlLogoSize() {
      document.querySelectorAll('header img[src*="TL-Logo"]').forEach(function (l) {
        if (window.innerWidth >= 700) {
          l.style.setProperty('height', '60px', 'important');
          l.style.setProperty('max-height', '60px', 'important');
          l.style.setProperty('width', 'auto', 'important');
          l.style.setProperty('max-width', 'none', 'important');
        } else {
          ['height', 'max-height', 'width', 'max-width'].forEach(function (p) { l.style.removeProperty(p); });
        }
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tlLogoSize);
    } else {
      tlLogoSize();
    }
    window.addEventListener('resize', tlLogoSize);
  })();

  /* TL: hide the Modern Fit button for Classic-only colors. */

  var TL_CLASSIC_ONLY_COLORS = [
    'Washed Quicksilver',
    'Washed Periwinkle'
  ];

  function tlFitHiderGetSelectedColor() {
    var vp = document.querySelector('.product-info variant-picker');
    if (!vp) { return ''; }
    var fieldsets = vp.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend');
      if (!legend || legend.textContent.indexOf('Color') === -1) { continue; }
      var checked = fieldsets[i].querySelector('input[type="radio"]:checked');
      if (!checked) { continue; }
      var lbl = checked.id ? document.querySelector('label[for="' + checked.id + '"]') : null;
      if (!lbl) { continue; }
      var sr = lbl.querySelector('.sr-only');
      return (sr ? sr : lbl).textContent.trim();
    }
    return '';
  }

  function tlFitHiderUpdate() {
    var vp = document.querySelector('.product-info variant-picker');
    if (!vp) { return; }

    var fitFieldset = null;
    var fieldsets = vp.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var leg = fieldsets[i].querySelector('legend');
      if (leg && leg.textContent.indexOf('Fit') !== -1) { fitFieldset = fieldsets[i]; break; }
    }
    if (!fitFieldset) { return; }

    var selectedColor = tlFitHiderGetSelectedColor();
    var isClassicOnly = TL_CLASSIC_ONLY_COLORS.indexOf(selectedColor) !== -1;

    var modernRadio = null, modernLabel = null, classicRadio = null;
    var inputs = fitFieldset.querySelectorAll('input[type="radio"]');
    for (var j = 0; j < inputs.length; j++) {
      var radio = inputs[j];
      var lbl = radio.id ? document.querySelector('label[for="' + radio.id + '"]') : null;
      if (!lbl) { continue; }
      var srEl = lbl.querySelector('.sr-only');
      var text = (srEl ? srEl : lbl).textContent.trim();
      if (text === 'Modern') { modernRadio = radio; modernLabel = lbl; }
      if (text === 'Classic') { classicRadio = radio; }
    }

    if (isClassicOnly) {
      if (modernRadio && modernRadio.checked && classicRadio && !classicRadio.checked) {
        classicRadio.click();
        return;
      }
      if (modernRadio) { modernRadio.style.setProperty('display', 'none', 'important'); }
      if (modernLabel) { modernLabel.style.setProperty('display', 'none', 'important'); }
    } else {
      if (modernRadio) { modernRadio.style.removeProperty('display'); }
      if (modernLabel) { modernLabel.style.removeProperty('display'); }
    }
  }

  /* TL: when a size chip that is currently CHECKED gets hidden, move the selection
     to the nearest visible, non-disabled size (Aug 4 audit fix H1). */

  function tlReselectNearestSize(sizeFieldset, hiddenInput) {
    if (!hiddenInput || !hiddenInput.checked) { return; }
    var inputs = Array.prototype.slice.call(sizeFieldset.querySelectorAll('input[type="radio"]'));
    var idx = inputs.indexOf(hiddenInput);
    var order = [];
    for (var d = 1; d < inputs.length; d++) {
      if (idx - d >= 0) { order.push(inputs[idx - d]); }
      if (idx + d < inputs.length) { order.push(inputs[idx + d]); }
    }
    for (var i = 0; i < order.length; i++) {
      var cand = order[i];
      var lbl = cand.id ? document.querySelector('label[for="' + cand.id + '"]') : null;
      if (!lbl) { continue; }
      var hidden = lbl.style.display === 'none' || getComputedStyle(lbl).display === 'none';
      var disabled = cand.disabled || lbl.classList.contains('is-disabled');
      if (!hidden && !disabled) { cand.click(); return; }
    }
  }

  /* TL: hide the 3XL size button when Modern fit is selected. */

  function tl3XLHiderUpdate() {
    var vp = document.querySelector('.product-info variant-picker');
    if (!vp) { return; }

    var fitFieldset = null, sizeFieldset = null;
    var fieldsets = vp.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var leg = fieldsets[i].querySelector('legend');
      if (!leg) { continue; }
      if (leg.textContent.indexOf('Fit') !== -1) { fitFieldset = fieldsets[i]; }
      if (leg.textContent.indexOf('Size') !== -1) { sizeFieldset = fieldsets[i]; }
    }
    if (!fitFieldset || !sizeFieldset) { return; }

    var modernActive = false;
    var fitInputs = fitFieldset.querySelectorAll('input[type="radio"]');
    for (var j = 0; j < fitInputs.length; j++) {
      var flbl = fitInputs[j].id ? document.querySelector('label[for="' + fitInputs[j].id + '"]') : null;
      if (!flbl) { continue; }
      var fsr = flbl.querySelector('.sr-only');
      var fname = (fsr ? fsr : flbl).textContent.trim();
      if (fname === 'Modern' && fitInputs[j].checked) { modernActive = true; break; }
    }

    var sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
    for (var k = 0; k < sizeInputs.length; k++) {
      var slbl = sizeInputs[k].id ? document.querySelector('label[for="' + sizeInputs[k].id + '"]') : null;
      if (!slbl) { continue; }
      var ssr = slbl.querySelector('.sr-only');
      var sname = (ssr ? ssr : slbl).textContent.trim();
      if (sname !== '3XL') { continue; }
      if (modernActive) {
        tlReselectNearestSize(sizeFieldset, sizeInputs[k]);
        sizeInputs[k].style.setProperty('display', 'none', 'important');
        slbl.style.setProperty('display', 'none', 'important');
      } else {
        sizeInputs[k].style.removeProperty('display');
        slbl.style.removeProperty('display');
      }
    }
  }

  /* TL: Modern sell-through sizes (Aug 4, Robbie rule). */

  var TL_SELL_THROUGH_MODERN_SIZES = ['S'];
  var tlProductData = null;

  function tlModernSellThroughUpdate() {
    if (!tlProductData) { return; }
    var vp = document.querySelector('.product-info variant-picker');
    if (!vp) { return; }

    var fitFieldset = null, sizeFieldset = null;
    var fieldsets = vp.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var leg = fieldsets[i].querySelector('legend');
      if (!leg) { continue; }
      if (leg.textContent.indexOf('Fit') !== -1) { fitFieldset = fieldsets[i]; }
      if (leg.textContent.indexOf('Size') !== -1) { sizeFieldset = fieldsets[i]; }
    }
    if (!fitFieldset || !sizeFieldset) { return; }

    var modernActive = false;
    var fitInputs = fitFieldset.querySelectorAll('input[type="radio"]');
    for (var j = 0; j < fitInputs.length; j++) {
      var flbl = fitInputs[j].id ? document.querySelector('label[for="' + fitInputs[j].id + '"]') : null;
      if (!flbl) { continue; }
      var fsr = flbl.querySelector('.sr-only');
      if ((fsr ? fsr : flbl).textContent.trim() === 'Modern' && fitInputs[j].checked) { modernActive = true; break; }
    }

    var selectedColor = tlFitHiderGetSelectedColor();

    /* .js endpoint options are objects ({name, position, values}) */
    var colorIdx = -1, fitIdx = -1, sizeIdx = -1;
    (tlProductData.options || []).forEach(function (opt, idx) {
      var n = String(opt && opt.name ? opt.name : opt).toLowerCase();
      if (n === 'color' || n === 'colour') { colorIdx = idx; }
      else if (n === 'fit') { fitIdx = idx; }
      else if (n === 'size') { sizeIdx = idx; }
    });
    if (colorIdx === -1 || fitIdx === -1 || sizeIdx === -1) { return; }

    TL_SELL_THROUGH_MODERN_SIZES.forEach(function (sz) {
      var live = false;
      if (modernActive) {
        live = (tlProductData.variants || []).some(function (v) {
          var opts = [v.option1, v.option2, v.option3];
          return v.available &&
            String(opts[colorIdx] || '').trim() === selectedColor &&
            String(opts[fitIdx] || '').trim() === 'Modern' &&
            String(opts[sizeIdx] || '').trim() === sz;
        });
      }
      var hide = modernActive && !live;
      var sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
      for (var k = 0; k < sizeInputs.length; k++) {
        var slbl = sizeInputs[k].id ? document.querySelector('label[for="' + sizeInputs[k].id + '"]') : null;
        if (!slbl) { continue; }
        var ssr = slbl.querySelector('.sr-only');
        if ((ssr ? ssr : slbl).textContent.trim() !== sz) { continue; }
        if (hide) {
          tlReselectNearestSize(sizeFieldset, sizeInputs[k]);
          sizeInputs[k].style.setProperty('display', 'none', 'important');
          slbl.style.setProperty('display', 'none', 'important');
        } else {
          sizeInputs[k].style.removeProperty('display');
          slbl.style.removeProperty('display');
        }
      }
    });
  }

  (function () {
    if (!document.querySelector('.product-info')) { return; }
    var m = window.location.pathname.match(/\/products\/([a-z0-9\-_]+)/i);
    if (!m) { return; }
    fetch('/products/' + m[1] + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d) { tlProductData = d; tlModernSellThroughUpdate(); }
      })
      .catch(function () {});
  })();

  /* TL: pre-order tidy (Aug 4, Robbie). */

  function tlPreorderTidy() {
    var items = document.querySelectorAll('.product-info__block-list > .product-info__block-item');
    var restockItem = null, preorderItem = null;
    for (var i = 0; i < items.length; i++) {
      var t = items[i].textContent || '';
      if (/re-?stocking soon/i.test(t)) { restockItem = items[i]; }
      if (/pre-?ordered item|shipment starts/i.test(t)) { preorderItem = items[i]; }
    }
    if (!restockItem) { return; }
    var fabVisible = false;
    if (preorderItem) {
      var fr = preorderItem.getBoundingClientRect();
      fabVisible = fr.height > 2;
    }
    if (fabVisible) {
      restockItem.style.setProperty('display', 'none', 'important');
    } else {
      restockItem.style.removeProperty('display');
    }
  }

  function tlPDPUpdate() {
    tlFitHiderUpdate();
    tl3XLHiderUpdate();
    tlModernSellThroughUpdate();
    tlPreorderTidy();
    setTimeout(tlPreorderTidy, 600);
  }

  document.addEventListener('change', function (event) {
    if (!event.target.closest) { return; }
    var radio = event.target.closest('.product-info variant-picker input[type="radio"]');
    if (!radio) { return; }
    setTimeout(tlPDPUpdate, 150);
  });

  (function () {
    var productInfo = document.querySelector('.product-info');
    if (!productInfo || !window.MutationObserver) { return; }
    var tlVPObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) { continue; }
          if (node.tagName && node.tagName.toLowerCase() === 'variant-picker') {
            tlPDPUpdate();
            return;
          }
          if (node.querySelector && node.querySelector('variant-picker')) {
            tlPDPUpdate();
            return;
          }
        }
      }
    });
    tlVPObserver.observe(productInfo, { childList: true, subtree: true });
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(tlPDPUpdate, 150);
    });
  } else {
    setTimeout(tlPDPUpdate, 150);
  }

  /* TL: WeFunder links open in a new tab (Aug 3) */

  document.addEventListener('click', function (event) {
    if (!event.target.closest) { return; }
    var wfLink = event.target.closest('a[href*="wefunder.com"]');
    if (wfLink) {
      wfLink.setAttribute('target', '_blank');
      wfLink.setAttribute('rel', 'noopener');
    }
  }, true);

  /* TL: buy-box star snippet gate (Aug 4, hardened).
     Hide the Okendo star rating in the buy box when the product has fewer than 10
     reviews. Okendo can RE-MOUNT its widget after initial render (seen on the
     hoodie PDP: gate hid the snippet, Okendo replaced it with a fresh visible
     node) -- so the observer stays alive for 15s and the gate re-applies on
     every mutation instead of disconnecting on first success. Scoped to the buy
     box; the reviews widget lower on the page is never touched. */

  (function () {
    var MIN_REVIEWS = 10;
    function tlStarGate() {
      var scope = document.querySelector('.product-info');
      if (!scope) { return; }
      var countEl = scope.querySelector('.oke-sr-count-number') || scope.querySelector('.oke-sr-count');
      if (!countEl) { return; }
      var n = parseInt(countEl.textContent.replace(/[^0-9]/g, ''), 10);
      if (isNaN(n) || n >= MIN_REVIEWS) { return; }
      var snippet = countEl.closest('.okendo-widget-container') ||
        countEl.closest('.okeReviews') ||
        countEl.closest('.oke-sr');
      if (snippet && snippet.style.display !== 'none') {
        snippet.style.setProperty('display', 'none', 'important');
      }
    }
    var scopeEl = document.querySelector('.product-info');
    if (!scopeEl) { return; }
    tlStarGate();
    if (!window.MutationObserver) { return; }
    var tlStarMo = new MutationObserver(function () { tlStarGate(); });
    tlStarMo.observe(scopeEl, { childList: true, subtree: true });
    setTimeout(function () { tlStarMo.disconnect(); tlStarGate(); }, 15000);
  })();

  /* TL: sticky-bar sold-out swap (Aug 4). */

  (function () {
    function tlStickySwap() {
      var bar = document.querySelector('.product-sticky-bar');
      if (!bar) { return; }
      var btn = bar.querySelector('button');
      if (!btn) { return; }
      var txt = (btn.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (txt === 'sold out') {
        btn.textContent = 'Join the Waitlist';
        btn.disabled = false;
        btn.classList.add('tl-sticky-waitlist');
      } else if (txt === 'join the waitlist') {
        btn.classList.add('tl-sticky-waitlist');
      } else {
        btn.classList.remove('tl-sticky-waitlist');
      }
    }
    document.addEventListener('click', function (e) {
      if (!e.target.closest) { return; }
      var b = e.target.closest('.tl-sticky-waitlist');
      if (!b) { return; }
      e.preventDefault();
      e.stopPropagation();
      var main = document.querySelector('.product-info');
      if (main) { main.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }, true);
    if (!window.MutationObserver) { tlStickySwap(); return; }
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var bar = document.querySelector('.product-sticky-bar');
      if (bar) {
        clearInterval(t);
        tlStickySwap();
        var mo = new MutationObserver(function () { tlStickySwap(); });
        mo.observe(bar, { childList: true, subtree: true, characterData: true });
      } else if (tries > 20) {
        clearInterval(t);
      }
    }, 500);
  })();

})();